import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Resizable } from 'react-resizable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HolderOutlined } from '@ant-design/icons';

let suppressHeaderSortUntil = 0;
export function isHeaderSortSuppressed(): boolean {
  return Date.now() < suppressHeaderSortUntil;
}

export interface ResizableTitleProps extends React.HTMLAttributes<HTMLTableCellElement> {
  onResize?: (e: React.SyntheticEvent, data: { size: { width: number } }) => void;
  width?: number;
  'data-col-slug'?: string;
}

export function ResizableTitle({
  onResize,
  width,
  'data-col-slug': colSlug,
  ...restProps
}: ResizableTitleProps) {
  const thRef = useRef<HTMLTableCellElement | null>(null);
  const sortable = useSortable({ id: colSlug ?? '__none__', disabled: !colSlug });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;
  const setRefs = useCallback(
    (node: HTMLTableCellElement | null) => {
      thRef.current = node;
      setNodeRef(node);
    },
    [setNodeRef],
  );

  const [liveWidth, setLiveWidth] = useState<number | undefined>(width);
  useEffect(() => {
    setLiveWidth(width);
  }, [width]);

  const applyDomWidth = useCallback((newWidth: number) => {
    const th = thRef.current;
    if (!th) return;
    const idx = th.cellIndex;
    if (idx < 0) return;
    const tableWrap = th.closest('.ant-table');
    if (!tableWrap) return;
    const colgroups = tableWrap.querySelectorAll('colgroup');
    colgroups.forEach((cg) => {
      const col = cg.children[idx] as HTMLElement | undefined;
      if (col) col.style.width = `${newWidth}px`;
    });
  }, []);

  const thStyle: React.CSSProperties = {
    ...restProps.style,
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
  };

  const resizeHandle = onResize ? (
    <span
      className="lens-resize-handle"
      style={{
        position: 'absolute',
        right: -5,
        top: 0,
        bottom: 0,
        width: 10,
        cursor: 'col-resize',
        zIndex: 4,
      }}
      onMouseDown={(e) => {
        suppressHeaderSortUntil = Date.now() + 400;
        e.stopPropagation();
        if (!width && thRef.current && onResize) {
          onResize(e, { size: { width: thRef.current.offsetWidth } });
        }
      }}
      onPointerDown={(e) => {
        suppressHeaderSortUntil = Date.now() + 400;
        e.stopPropagation();
      }}
      onClick={(e) => {
        suppressHeaderSortUntil = Date.now() + 400;
        e.stopPropagation();
      }}
    />
  ) : null;

  const { children, className, ...thRest } = restProps;
  const inner = colSlug ? (
    <div
      className="lens-col-header"
      style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}
    >
      <span
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        className="lens-col-drag"
        style={{ cursor: 'grab', display: 'inline-flex', flex: '0 0 auto', touchAction: 'none' }}
        aria-label="Переместить колонку"
      >
        <HolderOutlined />
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>{children}</span>
    </div>
  ) : (
    children
  );

  if (!width) {
    return (
      <th {...thRest} ref={setRefs} className={className} style={thStyle} data-col-slug={colSlug}>
        {inner}
        {resizeHandle}
      </th>
    );
  }

  return (
    <Resizable
      width={liveWidth ?? width!}
      height={0}
      handle={resizeHandle!}
      onResize={(_e, { size }) => {
        setLiveWidth(size.width);
        applyDomWidth(size.width);
      }}
      onResizeStart={() => {
        suppressHeaderSortUntil = Date.now() + 400;
      }}
      onResizeStop={(e, data) => {
        onResize?.(e, data);
      }}
      draggableOpts={{ enableUserSelectHack: false }}
      minConstraints={[50, 0]}
    >
      <th {...thRest} ref={setRefs} className={className} style={thStyle} data-col-slug={colSlug}>
        {inner}
      </th>
    </Resizable>
  );
}
